import React, { useEffect, useState } from 'react';
import { Table, Button, Row, Col, Badge, Dropdown, Modal, Form, Pagination } from 'react-bootstrap';
import { updateUsers, deleteUsers, getAllUsers } from '../../api/usersAPI';
import { toast } from 'react-toastify';
import Loader from '../Loader/Loader';
import DeleteIcon from '@mui/icons-material/Delete';

const UserCard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  let loggedInUser = JSON.parse(localStorage.getItem('user'));

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers(filter, currentPage, usersPerPage);
      setUsers(response);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter, currentPage, usersPerPage]);

  const getRoleTags = (user) => {
    const roles = [];
    if (user.isClient) roles.push({ label: 'Client', color: 'success' });
    if (user.isUser) roles.push({ label: 'User', color: 'primary' });
    if (user.isEmployee) roles.push({ label: 'Employee', color: 'warning' });
    return roles;
  };

  const initiateRoleChange = (userId, role) => {
    if (!loggedInUser) {
      loggedInUser = JSON.parse(localStorage.getItem('user'));
    }

    if (loggedInUser?._id === userId) {
      alert('You can\'t change your own role');
      return;
    }
    setPendingRoleChange({ userId, role });
    setShowModal(true);
  };

  const handleRoleChange = async (userId, role) => {
    try {
      const user = users.find((user) => user._id === userId);
      const updatedUser = {
        ...user,
        [role]: !user[role],
      };

      const updatedData = {
        [role]: updatedUser[role],
      };

      await updateUsers(userId, updatedData);
      fetchUsers();
    } catch (error) {
      setError(error.message);
    }
  };

  const confirmRoleChange = async () => {
    if (pendingRoleChange) {
      const { userId, role } = pendingRoleChange;
      await handleRoleChange(userId, role);
      setShowModal(false);
      setPendingRoleChange(null);
      toast.success("User's role changed successfully");
    }
  };

  const initiateDelete = (userId) => {
    setPendingDelete(userId);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUsers(userId);
      fetchUsers();
      setShowModal(false);
      toast.success('User deleted successfully');
    } catch (error) {
      setError(error.message);
    }
  };

  const confirmDelete = async () => {
    if (pendingDelete) {
      await handleDelete(pendingDelete);
      setPendingDelete(null);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setPendingRoleChange(null);
    setPendingDelete(null); // Reset delete state
  };

  function convertToLocalString(str) {
    if (!str) {
      return null
    }
    const date = new Date(str);
    return date.toLocaleDateString("en-GB").split('/').join('-');
  }

  return (
    <>
      <Row className='align-items-center mb-3'>
        <Col>
          <h1>Users</h1>
        </Col>
        <Col md="4">
          <Form.Control
            type="text"
            placeholder="Search by name or user ID"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
      {loading ? (
        <Loader />
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <>
          <Table striped bordered hover responsive className='table-sm' style={{ height: '800px' }}>
            <thead>
              <tr>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>PHONE NUMBER</th>
                <th>GENDER</th>
                <th>DOB</th>
                <th>ROLES</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {users?.length > 0 && users.map((user) => (
                <tr key={user._id}>
                  <td>{`${user.name} (${user.userId})`}</td>
                  <td>{user.email}</td>
                  <td>{user.phoneNumber}</td>
                  <td>{user.gender}</td>
                  <td>{convertToLocalString(user?.dob)}</td>
                  <td>
                    {getRoleTags(user).map((role, index) => (
                      <Badge key={index} bg={role.color} className="me-2">
                        {role.label}
                      </Badge>
                    ))}
                  </td>
                  <td className='d-flex'>
                    <div>
                      <Dropdown className="d-inline-block me-2">
                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                          Manage Roles
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          {!user.isClient && (
                            <Dropdown.Item onClick={() => initiateRoleChange(user._id, 'isClient')}>
                              Add Client
                            </Dropdown.Item>
                          )}
                          {!user.isUser && (
                            <Dropdown.Item onClick={() => initiateRoleChange(user._id, 'isUser')}>
                              Add User
                            </Dropdown.Item>
                          )}
                          {!user.isEmployee && (
                            <Dropdown.Item onClick={() => initiateRoleChange(user._id, 'isEmployee')}>
                              Add Employee
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    <Button
                      size='sm'
                      variant="danger"
                      onClick={() => initiateDelete(user._id)}
                      className="d-inline-block"
                    >
                      <DeleteIcon />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          <Row className="justify-content-end mb-3">
            <Col xs="auto">
              <Form.Select
                aria-label="Rows per page"
                value={usersPerPage}
                onChange={(e) => {
                  setUsersPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Pagination>
                <Pagination.Prev
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                />
                <Pagination.Item>{currentPage}</Pagination.Item>
                <Pagination.Next onClick={() => setCurrentPage((prev) => prev + 1)} />
              </Pagination>
            </Col>
          </Row>
        </>
      )}

      {/* Modal for role change or deletion confirmation */}
      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{pendingDelete ? 'Confirm User Deletion' : 'Confirm Role Change'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingDelete
            ? 'Are you sure you want to delete this user? This action cannot be undone.'
            : 'Are you sure you want to change this user\'s role?'}
        </Modal.Body>
        <Modal.Footer className='flex-column'>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={pendingDelete ? confirmDelete : confirmRoleChange}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserCard;
